import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API_URL } from '@/lib/utils';
import { Loader2, CheckCircle } from 'lucide-react';

const services = [
  'NABH Accreditation',
  'NABL Certification',
  'Healthcare Courses',
  'Digital Healthcare (EMR/HIS)',
  'Hospital Planning',
  'TPA & Empanelment',
  'CME & Workshops',
  'Other'
];

const LeadForm = ({ source = 'website', variant = 'default', onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/leads`, {
        ...formData,
        source
      });
      setSubmitted(true);
      toast.success('Thank you! We will contact you shortly.');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">Our team will contact you within 24 hours.</p>
      </div>
    );
  }

  const isCompact = variant === 'compact';

  return (
    <form onSubmit={handleSubmit} className={isCompact ? 'space-y-3' : 'space-y-4'} data-testid="lead-form">
      <div className={isCompact ? '' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium">Full Name *</Label>
          <Input
            id="name"
            name="name"
            placeholder="Enter your name"
            value={formData.name}
            onChange={handleChange}
            required
            className="h-11"
            data-testid="lead-name-input"
          />
        </div>
        {!isCompact && (
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email Address *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="h-11"
              data-testid="lead-email-input"
            />
          </div>
        )}
      </div>

      {isCompact && (
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
            className="h-11"
            data-testid="lead-email-input"
          />
        </div>
      )}

      <div className={isCompact ? '' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium">Phone Number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="+91 9876543210"
            value={formData.phone}
            onChange={handleChange}
            required
            className="h-11"
            data-testid="lead-phone-input"
          />
        </div>
        {!isCompact && (
          <div className="space-y-2">
            <Label htmlFor="service" className="text-sm font-medium">Service Interested In</Label>
            <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
              <SelectTrigger className="h-11" data-testid="lead-service-select">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service} value={service}>{service}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!isCompact && (
        <div className="space-y-2">
          <Label htmlFor="message" className="text-sm font-medium">Message (Optional)</Label>
          <Textarea
            id="message"
            name="message"
            placeholder="Tell us about your requirements..."
            value={formData.message}
            onChange={handleChange}
            rows={4}
            data-testid="lead-message-input"
          />
        </div>
      )}

      <Button 
        type="submit" 
        className="w-full h-12 bg-[#2eb24c] hover:bg-[#259a40] text-white font-semibold"
        disabled={loading}
        data-testid="lead-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Get Free Consultation'
        )}
      </Button>
      
      <p className="text-xs text-gray-500 text-center">
        By submitting, you agree to our privacy policy. We'll never share your information.
      </p>
    </form>
  );
};

export default LeadForm;
